// src/utils/permission-gate.js
// 2026-05-15 EXTRACTED from src/App.jsx as part of permission matrix test PR.
// Frontend mirror of backend/services/permission-gate.cjs.
//
// Why mirror not import:
//   - Backend uses CommonJS (.cjs), frontend uses ESM. Single .js shared module
//     would require build-time transformation OR dual-package hazard.
//   - Maps are tiny (20 lines), drift detected by tests AND incident replay.
//
// Sync discipline: any change к either file MUST be mirrored. Drift checker
// в test file fails CI если maps diverge.

import { isAdmin } from '../utils.js';

export const TABLE_PAGE_ACCESS = {
  'payments': ['payments', 'dailycalcs'],
  'customer-payments': ['customers', 'dailycalcs'],
  'crg-deals': ['crg', 'ftdsinfo'],
  'daily-cap': ['dailycap'],
  'daily-cap-overrides': ['dailycap'],
  'daily-cap-aff-caps': ['dailycap'],
  'deals': ['deals'],
  'partners': ['partners'],
  'conference-folders': ['partners'],
  'conference-partners': ['partners'],
  'ftd-entries': ['ftdsinfo'],
  'daily-calcs-data': ['dailycalcs'],
  'wallets': [], // admin-only: форму пишет AdminPanel (isAdmin), не страница settings
  'offers': ['deals'],
  'selling-offers': ['deals'],
  'selling-settings': ['deals'],
  'invoices': ['invoicegen'],
  'invoice-settings': ['invoicegen'],
  'gen-balances': ['genbalances'],
  'today-transactions': ['genbalances'],
  'tx-archive': ['genbalances'],
  'ds-archive': ['genbalances'],
  'daily-summary': ['overview', 'monthlystats'],
  'blitz-reports': ['monthlystats'],
  'blitz-reports-archive': ['monthlystats'],
};

// 2026-05-15 — Action-level mirror (mirrors backend ACTION_PAGE_ACCESS).
// Used by canPerformAction() для frontend UI gates (hide/disable buttons,
// skip auto-triggered API calls). MUST stay в sync с backend map — drift
// check в permission-gate.test.js fails CI on mismatch.
export const ACTION_PAGE_ACCESS = {
  'monthly-targets:read': ['settings'],
  'monthly-targets:write': ['settings'],
  'ftd:crg-match-notify': ['ftdsinfo'],
  'telegram:notify': ['settings'],
  'telegram:test': ['settings'],
  'autob2026:status-read': ['autob2026'],
  // 2026-05-15 Phase 2.1 — adversarial review #5 expansion
  'invoices:create-new': ['invoicegen'],
  'invoices:next-number': ['invoicegen'],
  'wallet:balance-lookup': ['genbalances', 'settings'],
  'payments:click-telemetry': ['payments', 'customers', 'dailycalcs'],
  'phone-check:lookup': ['phonecheck'],
  // 2026-10-14 (PR B «Невалиды CRG-лидов») — вид Invalids в Daily Calculations.
  'invalid-leads:read': ['dailycalcs'],
  'invalid-leads:mark': ['dailycalcs'],
  'invalid-rules:write': ['dailycalcs'],
};

const EMPTY_PAGE_ACCESS_FALLBACK = ['overview', 'settings'];

/**
 * Check if user can save (POST) к а given table.
 *
 * Used by App.jsx useEffect debouncedSave gates — prevents auto-save cascade
 * (the Aff 246 Telegram incident, PR#32).
 *
 * @param {string} table — table name (e.g., 'payments')
 * @param {object|null} user — { email, pageAccess: string[] }
 * @returns {boolean} — true if user should be allowed к auto-save
 */
export function canSaveTable(table, user) {
  if (!user || !user.email) return false;
  if (isAdmin(user.email)) return true;
  const allowed = TABLE_PAGE_ACCESS[table];
  if (!allowed) return false; // unknown table → deny by default
  const userPages = (Array.isArray(user.pageAccess) && user.pageAccess.length > 0)
    ? user.pageAccess
    : EMPTY_PAGE_ACCESS_FALLBACK;
  return allowed.some(p => userPages.includes(p));
}

/**
 * Check if user can perform а given action endpoint.
 *
 * Used для UI gates (hide/disable buttons) + frontend fetch-skip patterns.
 * Mirror of backend checkActionAccess. Same admin-bypass + fallback semantics.
 *
 * @param {string} action — action key (e.g., 'telegram:notify')
 * @param {object|null} user — { email, pageAccess: string[] }
 * @returns {boolean} — true if user should be allowed
 */
export function canPerformAction(action, user) {
  if (!user || !user.email) return false;
  if (isAdmin(user.email)) return true;
  const allowed = ACTION_PAGE_ACCESS[action];
  if (!allowed) return false;
  const userPages = (Array.isArray(user.pageAccess) && user.pageAccess.length > 0)
    ? user.pageAccess
    : EMPTY_PAGE_ACCESS_FALLBACK;
  return allowed.some(p => userPages.includes(p));
}
